export const UK_POSTCODE_REGEX = /^([A-Z][A-HJ-Y]?[0-9][A-Z0-9]? ?[0-9][A-Z]{2}|GIR ?0A{2})$/i;
const names = new Intl.DisplayNames(['en-GB'], {type: 'region'});
const COUNTRY_CODES = Array.from({length: 26 * 26}, (_, index) =>
  String.fromCharCode(65 + Math.floor(index / 26), 65 + index % 26))
  .filter(code => names.of(code) !== code && !['ZZ', 'AN', 'BU', 'CS', 'DD', 'FX', 'NT', 'SU', 'TP', 'YU', 'ZR'].includes(code));
const countryAliases = new Map(COUNTRY_CODES.flatMap(code => [[code.toLowerCase(), code], [names.of(code).toLowerCase(), code]]));
for (const name of ['uk', 'united kingdom', 'great britain', 'england', 'scotland', 'wales', 'northern ireland']) countryAliases.set(name, 'GB');
for (const name of ['usa', 'united states of america', 'united states']) countryAliases.set(name, 'US');
countryAliases.set('czech republic', 'CZ');
countryAliases.set('turkey', 'TR');
const text = value => typeof value === 'string' ? value.trim() : '';
export function resolveCountry(value) {
  const name = text(value).toLowerCase();
  return countryAliases.get(name) || (/^[a-z]{2}$/i.test(name) ? name.toUpperCase() : '');
}
export function formatMapAddress(value) {
  if (typeof value === 'string') return value.trim();
  if (!value) return '';
  return [value.fullAddress || value.address || value.line1, value.line2, value.town || value.city,
    value.county, value.postcode || value.postalCode, value.country].filter(Boolean).join(', ');
}
export function getMapAddresses(personal = {}, base = {}) {
  const registered = personal.businessAddress || base.businessAddress || '';
  const residential = {
    address: [personal.currentAddress, personal.currentTown, personal.currentCounty, personal.currentPostcode || personal.currentPostCode, personal.currentCountry].filter(Boolean).join(', '),
    postcode: text(personal.currentPostcode || personal.currentPostCode), country: text(personal.currentCountry),
  };
  return {
    residentialPostcode: residential,
    businessAddress: {address: formatMapAddress(registered), postcode: text(registered.postcode || registered.postalCode), country: text(registered.country)},
    correspondenceAddress: personal.correspondenceSameAsResidential ? {...residential} : {
      address: [personal.correspondenceAddressLine1, personal.correspondenceAddressLine2, personal.correspondenceTown,
        personal.correspondenceCounty, personal.correspondencePostcode, personal.correspondenceCountry].filter(Boolean).join(', '),
      postcode: text(personal.correspondencePostcode), country: text(personal.correspondenceCountry),
    },
  };
}
export function getPostalLocation(address = {}) {
  const full = text(address.address);
  const ukMatch = (full.toUpperCase().match(/\b(?:[A-Z][A-HJ-Y]?[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}|GIR\s?0A{2})\b/g) || []).find(value => UK_POSTCODE_REGEX.test(value));
  let country = resolveCountry(address.country);
  if (!country) {
    const ending = full.split(/[,\n]/).map(part => part.trim().toLowerCase());
    country = ending.map(part => countryAliases.get(part)).find(Boolean) || '';
  }
  let postcode = text(address.postcode);
  if (!country && !text(address.country) && UK_POSTCODE_REGEX.test(postcode || ukMatch || '')) country = 'GB';
  if (!postcode && country === 'GB') postcode = ukMatch || '';
  if (!postcode && country === 'US') postcode = full.match(/\b\d{5}(?:-\d{4})?\b/)?.[0] || '';
  if (!postcode && country === 'CA') postcode = full.toUpperCase().match(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/)?.[0] || '';
  if (!postcode && country && country !== 'GB') postcode = full.match(/\b\d{4,6}(?:-\d{3,4})?\b/)?.[0] || '';
  // Never interpret an overseas postal code as a UK postcode.
  return {country, postcode: postcode.toUpperCase().trim(), address: full};
}
const postalCache = new Map();
export async function geocodeInternationalPostalCode(country, postcode, fetcher = fetch) {
  const postal = country === 'CA' ? postcode.replace(/\s/g, '').slice(0, 3) : postcode;
  const key = `${country}:${postal}`;
  if (postalCache.has(key)) return postalCache.get(key);
  const request = (async () => {
    try {
      const response = await fetcher(`https://api.zippopotam.us/${encodeURIComponent(country.toLowerCase())}/${encodeURIComponent(postal)}`);
      if (!response.ok) return null;
      const data = await response.json();
      if (String(data['country abbreviation'] || '').toUpperCase() !== country) return null;
      const place = data.places?.[0];
      const latlng = place ? [Number(place.latitude), Number(place.longitude)] : null;
      return latlng && latlng.every(Number.isFinite) && Math.abs(latlng[0]) <= 90 && Math.abs(latlng[1]) <= 180 ? latlng : null;
    } catch { postalCache.delete(key); return null; }
  })();
  postalCache.set(key, request);
  return request;
}
